import { CSSProperties, KeyboardEvent, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { usePopover } from '../../../hooks/usePopover';
import { cn } from '../../../utils/cn';
import { CheckMark } from '../Checkbox/Checkbox';
import { controlClass, describedBy, Field, FieldOwnProps } from '../Field/Field';
import { Icon } from '../Icon/Icon';
import { Portal } from '../Portal/Portal';

export interface SelectOption<V = any> {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

const matches = (o: SelectOption, q: string) =>
  !q || String(o.label).toLowerCase().includes(q.toLowerCase()) || String(o.description ?? '').toLowerCase().includes(q.toLowerCase());

/** Shared keyboard + popover logic for Select and MultiSelect. */
interface ListboxOptions {
  options: SelectOption[];
  disabled?: boolean;
  closeOnPick: boolean;
  onPick: (o: SelectOption) => void;
  initialIndex: () => number;
}

function useListbox({ options, disabled, closeOnPick, onPick, initialIndex }: ListboxOptions) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => options.filter((o) => matches(o, query)), [options, query]);

  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    setActive(Math.max(0, initialIndex()));
    setOpen(true);
  };
  const close = (focusAnchor = true) => {
    setOpen(false);
    if (focusAnchor) anchorRef.current?.focus();
  };
  const pick = (o?: SelectOption) => {
    if (!o || o.disabled) return;
    onPick(o);
    if (closeOnPick) close();
  };

  useClickOutside([anchorRef, popRef], () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(filtered.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return { open, setOpen, openMenu, close, query, setQuery, active, setActive, filtered, anchorRef, popRef, listRef, searchRef, onKeyDown, pick };
}

interface ListPanelProps {
  lb: ReturnType<typeof useListbox>;
  style: CSSProperties;
  searchable?: boolean;
  listId: string;
  emptyText?: ReactNode;
  footer?: ReactNode;
  multi?: boolean;
  isSelected: (o: SelectOption) => boolean;
}

function ListPanel({ lb, style, searchable, listId, emptyText, footer, multi, isSelected }: ListPanelProps) {
  useEffect(() => {
    if (searchable) setTimeout(() => lb.searchRef.current?.focus(), 0);
  }, [searchable, lb.searchRef]);

  return (
    <Portal>
      <div ref={lb.popRef} style={style} className="overflow-hidden rounded-xl border border-line bg-white shadow-pop animate-pop">
        {searchable && (
          <div className="border-b border-line p-2">
            <div className="relative">
              <Icon name="search" size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                ref={lb.searchRef}
                value={lb.query}
                onChange={(e) => {
                  lb.setQuery(e.target.value);
                  lb.setActive(0);
                }}
                onKeyDown={lb.onKeyDown}
                placeholder="Search"
                aria-label="Search options"
                aria-controls={listId}
                className="h-9 w-full rounded-lg bg-navy-50 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-steel/30"
              />
            </div>
          </div>
        )}
        <ul ref={lb.listRef} id={listId} role="listbox" aria-multiselectable={multi || undefined} className="max-h-64 overflow-auto p-1">
          {lb.filtered.length === 0 && <li className="px-3 py-3 text-sm text-ink-400">{emptyText}</li>}
          {lb.filtered.map((o, i) => {
            const selected = isSelected(o);
            return (
              <li
                key={String(o.value)}
                id={`${listId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={selected}
                aria-disabled={o.disabled || undefined}
                onMouseEnter={() => lb.setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => lb.pick(o)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2.5',
                  i === lb.active && 'bg-navy-50',
                  o.disabled && 'cursor-not-allowed opacity-40',
                )}
              >
                {multi && <CheckMark checked={selected} />}
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate', selected && !multi && 'font-semibold text-navy-800')}>{o.label}</span>
                  {o.description && <span className="block truncate text-[13px] text-ink-400">{o.description}</span>}
                </span>
                {!multi && selected && <Icon name="check" size={18} className="shrink-0 text-steel" />}
              </li>
            );
          })}
        </ul>
        {footer}
      </div>
    </Portal>
  );
}

/**
 * Dropdown for one value.
 * options: [{ value, label, description?, disabled? }]
 */
export interface SelectProps<V = any> extends Omit<FieldOwnProps, 'labelAction'> {
  options?: SelectOption<V>[];
  value?: V | null;
  onChange?: (value: V | null, option: SelectOption<V> | null) => void;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  emptyText?: ReactNode;
  id?: string;
  'aria-label'?: string;
}

export function Select({
  label, hint, error, required, options = [], value, onChange, placeholder = 'Select',
  searchable, clearable, disabled, size = 'md', className, containerClassName, emptyText = 'Nothing found', id,
  'aria-label': ariaLabel,
}: SelectProps) {
  const autoId = useId();
  const btnId = id || autoId;
  const listId = `${btnId}-list`;
  const selected = options.find((o) => o.value === value);
  const isSearchable = searchable ?? options.length > 8;

  const lb = useListbox({
    options,
    disabled,
    closeOnPick: true,
    onPick: (o) => onChange?.(o.value, o),
    initialIndex: () => options.findIndex((o) => o.value === value),
  });
  const style = usePopover(lb.open, lb.anchorRef, lb.popRef, { matchWidth: true, deps: [lb.filtered.length] });

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={btnId} className={containerClassName}>
      <div className="relative">
        <button
          ref={lb.anchorRef}
          id={btnId}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={lb.open}
          aria-controls={lb.open ? listId : undefined}
          aria-activedescendant={lb.open && !isSearchable ? `${listId}-${lb.active}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(btnId, error, hint)}
          disabled={disabled}
          onClick={() => (lb.open ? lb.setOpen(false) : lb.openMenu())}
          onKeyDown={lb.onKeyDown}
          className={cn(
            controlClass({ error, disabled, open: lb.open }),
            'flex items-center gap-2 pl-3.5 text-left',
            clearable && selected ? 'pr-16' : 'pr-2.5',
            size === 'sm' ? 'h-9 text-sm' : 'h-10 text-sm',
            className,
          )}
        >
          <span className={cn('flex-1 truncate', !selected && 'text-ink-300')}>{selected ? selected.label : placeholder}</span>
          <Icon name="chevronDown" size={18} className={cn('shrink-0 text-ink-400 transition-transform', lb.open && 'rotate-180')} />
        </button>
        {clearable && selected && !disabled && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => onChange?.(null, null)}
            className="absolute right-9 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 hover:bg-navy-50 hover:text-navy-800"
          >
            <Icon name="x" size={16} />
          </button>
        )}
      </div>
      {lb.open && (
        <ListPanel lb={lb} style={style} searchable={isSearchable} listId={listId} emptyText={emptyText} isSelected={(o) => o.value === value} />
      )}
    </Field>
  );
}

/** Dropdown for many values. `value` is an array. */
export interface MultiSelectProps<V = any> extends Omit<FieldOwnProps, 'labelAction'> {
  options?: SelectOption<V>[];
  value?: V[];
  onChange?: (value: V[]) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  maxChips?: number;
  emptyText?: ReactNode;
  id?: string;
}

export function MultiSelect({
  label, hint, error, required, options = [], value = [], onChange, placeholder = 'Select',
  searchable, disabled, maxChips = 2, containerClassName, emptyText = 'Nothing found', id,
}: MultiSelectProps) {
  const autoId = useId();
  const btnId = id || autoId;
  const listId = `${btnId}-list`;
  const isSearchable = searchable ?? options.length > 6;
  const selectedOpts = options.filter((o) => value.includes(o.value));

  const toggle = (o: SelectOption) => onChange?.(value.includes(o.value) ? value.filter((v) => v !== o.value) : [...value, o.value]);
  const lb = useListbox({ options, disabled, closeOnPick: false, onPick: toggle, initialIndex: () => 0 });
  const style = usePopover(lb.open, lb.anchorRef, lb.popRef, { matchWidth: true, deps: [lb.filtered.length, value.length] });

  const allVisibleSelected = lb.filtered.length > 0 && lb.filtered.every((o) => o.disabled || value.includes(o.value));
  const selectVisible = () => {
    const ids = lb.filtered.filter((o) => !o.disabled).map((o) => o.value);
    onChange?.(allVisibleSelected ? value.filter((v) => !ids.includes(v)) : [...new Set([...value, ...ids])]);
  };

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={btnId} className={containerClassName}>
      <button
        ref={lb.anchorRef}
        id={btnId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={lb.open}
        aria-controls={lb.open ? listId : undefined}
        aria-activedescendant={lb.open && !isSearchable ? `${listId}-${lb.active}` : undefined}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        onClick={() => (lb.open ? lb.setOpen(false) : lb.openMenu())}
        onKeyDown={lb.onKeyDown}
        className={cn(controlClass({ error, disabled, open: lb.open }), 'flex min-h-[40px] items-center gap-2 py-1 pl-2 pr-2.5 text-left')}
      >
        <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
          {selectedOpts.length === 0 && <span className="pl-1.5 text-ink-300">{placeholder}</span>}
          {selectedOpts.slice(0, maxChips).map((o) => (
            <span key={String(o.value)} className="max-w-[10rem] truncate rounded-lg bg-navy-50 px-2 py-1 text-sm font-medium text-navy-800">
              {o.label}
            </span>
          ))}
          {selectedOpts.length > maxChips && (
            <span className="rounded-lg bg-brand-50 px-2 py-1 text-sm font-medium text-brand-700">+{selectedOpts.length - maxChips} more</span>
          )}
        </span>
        <Icon name="chevronDown" size={18} className={cn('shrink-0 text-ink-400 transition-transform', lb.open && 'rotate-180')} />
      </button>
      {lb.open && (
        <ListPanel
          lb={lb}
          style={style}
          multi
          searchable={isSearchable}
          listId={listId}
          emptyText={emptyText}
          isSelected={(o) => value.includes(o.value)}
          footer={
            <div className="flex items-center justify-between border-t border-line px-3 py-2 text-sm">
              <button type="button" onClick={selectVisible} className="font-medium text-steel hover:underline">
                {allVisibleSelected ? 'Unselect all' : 'Select all'}
              </button>
              <span className="text-ink-400">{value.length} selected</span>
              <button type="button" onClick={() => lb.close()} className="font-medium text-navy-800 hover:underline">
                Done
              </button>
            </div>
          }
        />
      )}
    </Field>
  );
}

export default Select;
